import styled from 'styled-components';
import MediaQuery from 'react-responsive';

import { useCallback, useMemo } from 'react';
import {
  blackPearl_6,
  blueMana,
  paradigmLightBlue,
  tangaroa,
} from '@paradigm/design-system/src/colors';
import {
  BOTTOM_TABS,
  Tab,
  useBottomTabsMethods,
  useBottomTabsState,
} from '@paradigm/features/src/unified-rfqs/ui/bottom-tabs';
import useSyncBottomTabWithPinMode from '#/components/UnifiedRfqs/useSyncBottomTabWithPinMode';
import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import {
  FEATURE_BLOCK_TERMINAL,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';
import TradesHistory from '#/components/UnifiedRfqs/trade-history/TradesHistory';
import TradesTape from '#/components/UnifiedRfqs/trade-tape/TradesTape';
import OpenOrders from '#/components/UnifiedRfqs/open-orders/OpenOrders';
import OrdersHistoryTable from '#/components/UnifiedRfqs/orders-history/OrdersHistoryTable';
import RfqsHistoryTable from '#/components/UnifiedRfqs/rfqs-history/RfqsHistoryTable';
import PositionsTab from '#/components/UnifiedRfqs/positions/PositionsTab';
import BlockTerminalTab from '#/components/Terminal/BlockTerminalTab';
import { useBottomTabsToggleState } from '#/contexts/BottomTabsStateProvider';
import { UnifiedRfqTradeTapeProvider } from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';
import { LocalTabFiltersProvider } from '#/components/UnifiedRfqs/UnifiedRfqs';
import CloseIcon from '@paradigm/design-system/src/assets/CloseIcon';
import MobilePageSelect from '#/components/UnifiedRfqs/components/MobilePageSelect';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import Badge from '@paradigm/design-system/src/components/Badge';
import { useUnifiedOpenOrdersCount } from '@paradigm/features/src/unified-rfqs/contexts/unified-orders';
import TabGroup from '@paradigm/design-system/src/components/TabGroup';
import { useTrackAction } from '@paradigm/features/src/analytics';

export { FEATURE_BLOCK_TERMINAL } from '#/features/terminal/drawer-context';

const ALL_TABS_OPTIONS = BOTTOM_TABS.map((tab) => ({
  value: tab.id,
  label: tab.label,
}));

const ALL_RENDER_BOTTOM_TABS = BOTTOM_TABS.map((tab) => {
  if (tab.id === 'open-orders')
    return {
      id: 'open-orders',
      label: (
        <>
          {tab.label}
          <OpenOrdersCountBadge />
        </>
      ),
    } as const;
  return tab;
});

export function getTabsOptionsForFeature(isBlockTerminalEnabled: boolean) {
  return isBlockTerminalEnabled
    ? ALL_TABS_OPTIONS
    : ALL_TABS_OPTIONS.filter((t) => t.value !== 'block-terminal');
}

export function getRenderBottomTabsForFeature(isBlockTerminalEnabled: boolean) {
  return isBlockTerminalEnabled
    ? ALL_RENDER_BOTTOM_TABS
    : ALL_RENDER_BOTTOM_TABS.filter((t) => t.id !== 'block-terminal');
}

function OpenOrdersCountBadge() {
  const ordersCount = useUnifiedOpenOrdersCount();
  if (ordersCount === 0) return null;
  return (
    <Badge variant="count" position="relative" margin="0 0 0 8px">
      {ordersCount}
    </Badge>
  );
}

export default function BottomPanel() {
  const { setTab } = useBottomTabsMethods();
  const tab = useBottomTabsState();
  const { toggle } = useBottomTabsToggleState();
  const trackAction = useTrackAction();
  const isBlockTerminalEnabled = useIsFeatureFlagEnabled(
    FEATURE_BLOCK_TERMINAL,
  );
  const drawerState = useTerminalDrawerState();
  const sidePanelOpen =
    drawerState.pinMode === 'side-panel' && drawerState.isOpen;
  // Slot stays mounted whenever the flag is on so chat history and the
  // agent connection survive a pin-mode flip. Only the tab strip entry
  // is hidden in side mode (the terminal is visible in the side panel).
  const showBottomTabBlockTerminal = isBlockTerminalEnabled;
  const showBlockTerminalTabInStrip = isBlockTerminalEnabled && !sidePanelOpen;

  const tabsOptions = useMemo(
    () => getTabsOptionsForFeature(showBlockTerminalTabInStrip),
    [showBlockTerminalTabInStrip],
  );
  const renderBottomTabs = useMemo(
    () => getRenderBottomTabsForFeature(showBlockTerminalTabInStrip),
    [showBlockTerminalTabInStrip],
  );

  useSyncBottomTabWithPinMode({
    pinMode: drawerState.pinMode,
    isOpen: drawerState.isOpen,
    tab,
    isBlockTerminalEnabled,
    setTab,
  });

  const handleSelectTab = useCallback(
    (tabName: Tab) => {
      trackAction('select tab', { tabName });
      setTab(tabName);
    },
    [setTab, trackAction],
  );

  return (
    <Container>
      <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
        <SelectionContainer>
          <MobilePageSelect
            defaultValue={tabsOptions.find((t) => t.value === tab)}
            options={tabsOptions}
            onChange={(o) => {
              if (o != null) handleSelectTab(o.value);
            }}
          />
          <CloseIconContainer>
            <CloseIcon
              width={20}
              height={20}
              className="close-icon"
              color={blueMana}
              onClick={toggle}
            />
          </CloseIconContainer>
        </SelectionContainer>
      </MediaQuery>
      <MediaQuery minWidth={MOBILE_BREAKPOINT}>
        <TabGroup
          tabs={renderBottomTabs}
          activeTabId={tab}
          onSetTab={handleSelectTab}
          rightContent={
            <ViewStatsInfo>
              <span>
                <ShortcutKey>[F1]</ShortcutKey> Leaderboard
              </span>
            </ViewStatsInfo>
          }
        />
      </MediaQuery>
      {tab === 'positions' && <PositionsTab />}
      {tab === 'rfqs-history' && <RfqsHistoryTable showDrafts={false} />}
      {tab === 'rfqs-drafts' && <RfqsHistoryTable showDrafts />}
      {tab === 'open-orders' && <OpenOrders />}
      {tab === 'trade-tape' && <TradesTape />}
      {tab === 'order-history' && <OrdersHistoryTable />}
      {tab === 'trade-history' && <TradesHistory />}
      {/*
        Mount the Block Terminal whenever the flag is on (not just when the
        tab is visible) so the chat history, agent connection, and pending
        messages survive a tab switch. CSS toggles visibility instead of
        unmount/remount.

        In bottom-tab mode, shows when tab is 'block-terminal'.
        In side-panel mode, hidden here (shown in BlockTerminalSidePanel instead)
        but still mounted so context/history persist.
      */}
      {showBottomTabBlockTerminal && (
        <BlockTerminalSlot $active={tab === 'block-terminal'}>
          <LocalTabFiltersProvider storageKey="trade-tape-rfq-tab-filters">
            <UnifiedRfqTradeTapeProvider>
              <BlockTerminalTab />
            </UnifiedRfqTradeTapeProvider>
          </LocalTabFiltersProvider>
        </BlockTerminalSlot>
      )}
    </Container>
  );
}

const ShortcutKey = styled.span`
  color: ${paradigmLightBlue};
  font-weight: 600;
  font-family: monospace;
`;

const ViewStatsInfo = styled.div`
  height: 30px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background-color: transparent;
  padding: 0 12px 0 6px;
  color: rgba(255, 255, 255, 0.7);
  background-color: ${blackPearl_6};
  font-size: 0.8125rem;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const SelectionContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  height: 60px;
  background-color: ${tangaroa};
`;

const CloseIconContainer = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
`;

const Container = styled.div`
  flex: 1;
  /* See SplitScreen.BottomView — same reason: lets a flex child with
     internal overflow scroll instead of pushing the panel taller than
     its allocation. */
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    z-index: 13;
  }
`;

const BlockTerminalSlot = styled.div<{ $active: boolean }>`
  /* Matches BottomPanel Container's property for consistent typing */
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;
